import { motion } from 'framer-motion'
import { Link, useLocation } from '@tanstack/react-router'
import { LayoutGrid, Gift, Wallet, X, ShoppingCart, Receipt, LineChart, Settings2, LogOut, MessageCircle } from 'lucide-react'
import { useAppDispatch } from '../../store'
import { toggleSidebar } from '../../store/slices/appSlice'
import { useTranslation } from '../../hooks/useTranslation'
import { useDesignSystem } from '@/hooks/useDesignSystem'

export function Sidebar({ open }: { open: boolean }) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { animations, patterns } = useDesignSystem()
  const location = useLocation()

  const navItems = [
    { to: '/dashboard/dash', labelKey: 'common.navigation.dashboard', Icon: LineChart },
    { to: '/dashboard/services/my-services', labelKey: 'pages.services.myServices', Icon: LayoutGrid },
    { to: '/dashboard/services/browse', labelKey: 'pages.services.browse', Icon: ShoppingCart },
    { to: '/dashboard/referrals', labelKey: 'common.navigation.referrals', Icon: Gift },
    { to: '/dashboard/funds/wallet', labelKey: 'common.navigation.wallet', Icon: Wallet },
    { to: '/dashboard/funds/transactions', labelKey: 'pages.wallet.transactions', Icon: Receipt },
  ]

  const handleLogout = () => {
    localStorage.removeItem('token')
    window.location.href = '/register'
  }

  const isActive = (path: string) => location.pathname === path

  return (
    <motion.aside
      initial={false}
      animate={open ? 'open' : 'closed'}
      variants={animations.sidebar.sidebar}
      className={'fixed left-0 top-0 z-50 h-screen w-64 border-r border-border backdrop-blur-sm shadow-xl lg:shadow-none ' + patterns.glassDark}
    >
      <div className="flex h-full flex-col">
        {/* Header */}
          <div className="flex items-center justify-between border-b border-border p-4">
            <div className="flex items-center gap-3">
            <div className={patterns.utils.cx('h-8 w-8 flex items-center justify-center overflow-hidden', patterns.radius.xl, 'bg-gradient-to-br from-primary-300 to-transparent text-primary-foreground')}>
              <img src="/nexa-logo.jpg" alt="Nexa" className="w-full h-full object-cover" />
            </div>
            <span className={'text-xl font-bold bg-gradient-to-r from-primary-800 to-primary bg-clip-text ' + patterns.text.gradient}>
              Nexa
            </span>
          </div>
          <button
            onClick={() => dispatch(toggleSidebar())}
            className={'lg:hidden p-1 hover:bg-muted transition-colors ' + patterns.radius.xl}
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 p-4">
          {navItems.map(({ to, labelKey, Icon }) => (
            <motion.div key={to} variants={animations.sidebar.navLink} whileHover="hover" whileTap="tap">
              <Link
                to={to}
                className={patterns.utils.cx(
                  patterns.nav.link,
                  isActive(to) ? patterns.nav.linkActive : patterns.nav.linkInactive
                )}
                onClick={() => window.innerWidth < 1024 && dispatch(toggleSidebar())}
              >
                <Icon size={18} className="shrink-0" />
                <span className="font-bold">{t(labelKey)}</span>
              </Link>
            </motion.div>
          ))}
        </nav>

        {/* Contact Us & Logout Section */}
        <div className="px-4 pb-2 space-y-1">
          <motion.a
            variants={animations.sidebar.navLink}
            whileHover="hover"
            whileTap="tap"
            href="https://t.me/NEXA_TRADE_SUPPORT"
            target="_blank"
            rel="noopener noreferrer"
            className={patterns.utils.cx(
              patterns.nav.link,
              'w-full text-left text-primary hover:text-primary-foreground hover:bg-primary/20'
            )}
          >
            <MessageCircle size={18} className="shrink-0" />
            <span className="font-bold">{t('common.navigation.contactUs')}</span>
          </motion.a>
          <motion.button
            variants={animations.sidebar.navLink}
            whileHover="hover"
            whileTap="tap"
            onClick={handleLogout}
            className={patterns.utils.cx(
              patterns.nav.link,
              'w-full text-left text-destructive hover:text-destructive-foreground hover:bg-destructive/20'
            )}
          >
            <LogOut size={18} className="shrink-0" />
            <span className="font-bold">{t('common.navigation.logout')}</span>
          </motion.button>
        </div>

        {/* Account Section */}
        <div className="mb-5">
        <div className="flex items-center justify-between border-b border-border p-4">
            <div className="flex items-center gap-3">
              <div className={patterns.utils.cx('h-8 w-8 flex items-center justify-center', patterns.radius.full, 'bg-gradient-to-br from-primary to-transparent text-primary-foreground')}>
                <span className="text-sm font-bold">A</span>
              </div>
              <span className={'font-bold bg-gradient-to-r from-primary-800 to-primary bg-clip-text ' + patterns.text.gradient}>
                {t('components.header.account')}
              </span>
            </div>

            <Link to={'/dashboard/profile'} className={'hover:bg-muted p-2 ' + patterns.radius.xl}>
              <Settings2 size={20} className="text-primary-300 hover:text-primary-100 transition-colors" />
            </Link>
          </div>
        </div>
      </div>
    </motion.aside>
  )
}